import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PluginCard from '../components/PluginCard';
import CustomWorkForm from '../components/CustomWorkForm';
import Spline3D from '../components/Spline3D';
import { categories, stats, testimonials } from '../mock';
import { usePlugins } from '../hooks/usePlugins';
import { useAuth } from '../hooks/useAuth';
import { logout } from '../services/authService';
import {
  Search,
  ArrowRight,
  Zap,
  Shield,
  Clock,
  Users,
  MessageSquare,
  Youtube,
  Star,
  Sparkles,
  LogOut,
  User,
  CheckCircle,
  Tag,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Home = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const { plugins: allPlugins, loading, error } = usePlugins();
  const [filteredPlugins, setFilteredPlugins] = useState([]);
  
  const { user } = useAuth();

  useEffect(() => {
    let filtered = allPlugins;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(
        (p) =>
          p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.tagline?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredPlugins(filtered);
  }, [selectedCategory, searchQuery, allPlugins]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ background: '#0B0F13', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="text-center">
          <div className="animate-spin w-16 h-16 border-4 border-[#00FFD1] border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-white text-lg">Loading plugins from Firebase...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: '#0B0F13', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="text-center">
          <p className="text-red-400 text-lg mb-4">Error loading plugins: {error}</p>
          <Button onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#0B0F13', minHeight: '100vh' }}>
      {/* Header with Glassmorphism */}
      <header
        className="fixed top-0 w-full z-50 glass-strong"
        style={{
          borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
          padding: '16px 7.6923%',
          height: '80px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
          <Sparkles className="w-8 h-8 glow-cyan" style={{ color: '#00FFD1' }} />
          <span className="text-2xl font-bold text-glow" style={{ color: '#FFFFFF', fontWeight: 700 }}>
            Elite Plugin Hub
          </span>
        </div>

        <div className="flex items-center gap-6">
          {/* User Info */}
          <div className="flex items-center gap-2 text-sm" style={{ color: '#00FFD1' }}>
            <User className="w-4 h-4" />
            <span>{user?.displayName || user?.email}</span>
            {user?.emailVerified && <CheckCircle className="w-4 h-4" />}
          </div>

          <nav className="hidden md:flex items-center gap-8">
            <a
              href="#plugins"
              className="text-lg transition-colors duration-300"
              style={{ color: '#4D4D4D', fontWeight: 400 }}
              onMouseEnter={(e) => (e.target.style.color = '#FFFFFF')}
              onMouseLeave={(e) => (e.target.style.color = '#4D4D4D')}
            >
              Plugins
            </a>
            <a
              href="#custom-work"
              className="text-lg transition-colors duration-300"
              style={{ color: '#4D4D4D', fontWeight: 400 }}
              onMouseEnter={(e) => (e.target.style.color = '#FFFFFF')}
              onMouseLeave={(e) => (e.target.style.color = '#4D4D4D')}
            >
              Custom Work
            </a>
            <a
              href="#contact"
              className="text-lg transition-colors duration-300"
              style={{ color: '#4D4D4D', fontWeight: 400 }}
              onMouseEnter={(e) => (e.target.style.color = '#FFFFFF')}
              onMouseLeave={(e) => (e.target.style.color = '#4D4D4D')}
            >
              Contact
            </a>
          </nav>

          <Button
            onClick={handleLogout}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#FFFFFF',
              borderRadius: '0px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: 500,
              border: 'none',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 59, 48, 0.2)';
              e.target.style.color = '#FF3B30';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.1)';
              e.target.style.color = '#FFFFFF';
            }}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Hero Section with 3D and Premium Effects */}
      <section
        className="relative overflow-hidden mesh-gradient noise-overlay"
        style={{ paddingTop: '80px', minHeight: '100vh', display: 'flex', alignItems: 'center' }}
      >

        <div className="container mx-auto px-8 md:px-16 py-20 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Hero Content */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h1
                  className="text-6xl md:text-7xl font-bold leading-tight text-glow"
                  style={{
                    color: '#FFFFFF',
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.1,
                    fontFamily: 'Orbitron, Inter, sans-serif',
                  }}
                >
                  Elite Plugin Hub
                </h1>
                <p
                  className="text-3xl md:text-4xl font-semibold animate-gradient"
                  style={{
                    background: 'linear-gradient(90deg, #0EA5FF 0%, #8B5CF6 50%, #00FFD1 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    fontWeight: 600,
                  }}
                >
                  Free & Premium Minecraft Plugins
                </p>
              </div>

              <p className="text-xl" style={{ color: 'rgba(255, 255, 255, 0.85)', lineHeight: 1.5 }}>
                High-performance plugins, trusted licenses, and custom builds. Instant downloads + live Discord support.
              </p>

              {/* Welcome Message for Authenticated User */}
              {user && (
                <div className="flex items-center gap-3 p-4 glass" style={{ borderRadius: '0px', border: '1px solid rgba(0, 255, 209, 0.3)' }}>
                  <CheckCircle className="w-6 h-6" style={{ color: '#00FFD1' }} />
                  <div>
                    <p className="font-semibold" style={{ color: '#FFFFFF' }}>
                      Welcome back, {user.displayName || user.email}!
                    </p>
                    <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                      {user.emailVerified 
                        ? 'Your email is verified ✓' 
                        : 'Please verify your email to access all features'
                      }
                    </p>
                  </div>
                </div>
              )}

              {/* CTAs with Premium Glow */}
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => document.getElementById('plugins').scrollIntoView({ behavior: 'smooth' })}
                  className="animate-pulse-glow relative overflow-hidden group"
                  style={{
                    background: '#00FFD1',
                    color: '#000000',
                    borderRadius: '0px',
                    padding: '16px 32px',
                    fontSize: '18px',
                    fontWeight: 600,
                    minHeight: '56px',
                    border: 'none',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    fontFamily: 'Orbitron, Inter, sans-serif',
                  }}
                >
                  <span className="relative z-10">Browse Plugins</span>
                  <ArrowRight className="w-5 h-5 ml-2 inline relative z-10" />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-30 transition-opacity duration-500" 
                       style={{ transform: 'translateX(-100%)', animation: 'shimmer 2s infinite' }} />
                </Button>
                <Button
                  onClick={() => document.getElementById('custom-work').scrollIntoView({ behavior: 'smooth' })}
                  className="glass relative group"
                  style={{
                    color: '#FFFFFF',
                    borderRadius: '0px',
                    padding: '16px 32px',
                    fontSize: '18px',
                    fontWeight: 600,
                    minHeight: '56px',
                    border: '1px solid rgba(0, 255, 209, 0.3)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    fontFamily: 'Orbitron, Inter, sans-serif',
                  }}
                >
                  Request Custom Work
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div
                      className="text-3xl font-semibold mb-1"
                      style={{ color: '#00FFD1', fontWeight: 600 }}
                    >
                      {stat.value}
                    </div>
                    <div className="text-sm" style={{ color: '#4D4D4D' }}>
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Premium Spline 3D Animation */}
            <div className="relative flex items-center justify-center">
              <div className="animate-float">
                <Spline3D />
              </div>
              
              {/* Ambient glow effect around Spline */}
              <div 
                className="absolute inset-0 blur-3xl opacity-40 pointer-events-none"
                style={{
                  background: 'radial-gradient(circle, rgba(14, 165, 255, 0.4) 0%, rgba(139, 92, 246, 0.3) 50%, transparent 70%)',
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section style={{ padding: '100px 7.6923%', background: '#000000' }}>
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-5xl font-semibold mb-4"
              style={{ color: '#FFFFFF', fontWeight: 600, letterSpacing: '-0.02em' }}
            >
              Why Choose Elite Plugin Hub?
            </h2>
            <p className="text-xl" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
              Premium quality, instant delivery, and 24/7 support
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              {
                icon: Zap,
                title: 'Instant Downloads',
                description: 'Get your plugins immediately after purchase. No waiting, no hassle.',
              },
              {
                icon: Shield,
                title: 'Trusted & Secure',
                description: 'All plugins are tested, verified, and come with guaranteed updates.',
              },
              {
                icon: Clock,
                title: '24/7 Support',
                description: 'Live Discord support from our team of Minecraft plugin experts.',
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="glass p-8 text-center transition-all duration-400 hover:scale-105 group relative overflow-hidden"
                style={{
                  borderRadius: '0px',
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                     style={{
                       background: `radial-gradient(circle at 50% 50%, ${index === 0 ? 'rgba(14, 165, 255, 0.1)' : index === 1 ? 'rgba(139, 92, 246, 0.1)' : 'rgba(0, 255, 209, 0.1)'} 0%, transparent 70%)`,
                     }} />
                <feature.icon
                  className={`w-16 h-16 mx-auto mb-6 relative z-10 ${index === 0 ? 'glow-blue' : index === 1 ? 'glow-purple' : 'glow-cyan'}`}
                  style={{ color: index === 0 ? '#0EA5FF' : index === 1 ? '#8B5CF6' : '#00FFD1' }}
                />
                <h3
                  className="text-2xl font-bold mb-4 relative z-10"
                  style={{ color: '#FFFFFF', fontWeight: 700, fontFamily: 'Orbitron, Inter, sans-serif' }}
                >
                  {feature.title}
                </h3>
                <p className="text-lg relative z-10" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plugins Section */}
      <section id="plugins" style={{ padding: '100px 7.6923%', background: '#0B0F13' }}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2
              className="text-5xl font-bold mb-4 text-glow-blue"
              style={{ color: '#FFFFFF', fontWeight: 800, letterSpacing: '-0.02em', fontFamily: 'Orbitron, Inter, sans-serif' }}
            >
              Browse Premium Plugins
            </h2>
            <p className="text-xl" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
              High-quality plugins for every server type
            </p>
          </div>

          {/* Search and Filter with Glassmorphism */}
          <div className="mb-12 space-y-6">
            <div className="relative max-w-2xl mx-auto">
              <Search
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5"
                style={{ color: '#00FFD1' }}
              />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search plugins..."
                className="glass focus:glow-cyan transition-all duration-300"
                style={{
                  color: '#FFFFFF',
                  borderRadius: '0px',
                  padding: '18px 18px 18px 52px',
                  fontSize: '18px',
                  width: '100%',
                  border: '1px solid rgba(0, 255, 209, 0.3)',
                }}
              />
            </div>

            {/* Categories */}
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((cat) => {
                const IconComponent = require('lucide-react')[cat.icon];
                return (
                  <Button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={selectedCategory === cat.id ? 'glow-cyan' : 'glass hover:glass-strong'}
                    style={{
                      background: selectedCategory === cat.id ? '#00FFD1' : 'transparent',
                      color: selectedCategory === cat.id ? '#000000' : '#FFFFFF',
                      borderRadius: '0px',
                      padding: '12px 24px',
                      fontSize: '16px',
                      fontWeight: 600,
                      border: selectedCategory === cat.id ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      fontFamily: 'Orbitron, Inter, sans-serif',
                    }}
                  >
                    <IconComponent className="w-4 h-4 mr-2 inline" />
                    {cat.name}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Plugin Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPlugins.map((plugin) => (
              <PluginCard key={plugin.id} plugin={plugin} />
            ))}
          </div>

          {filteredPlugins.length === 0 && !loading && (
            <div className="text-center py-20">
              <p className="text-2xl" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                No plugins found. Try a different search or category.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Custom Work Section */}
      <section id="custom-work" style={{ padding: '100px 7.6923%', background: '#000000' }}>
        <div className="container mx-auto max-w-4xl">
          <CustomWorkForm />
        </div>
      </section>

      {/* Testimonials */}
      <section style={{ padding: '100px 7.6923%', background: '#0B0F13' }}>
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-5xl font-semibold mb-4"
              style={{ color: '#FFFFFF', fontWeight: 600, letterSpacing: '-0.02em' }}
            >
              Trusted by Server Owners
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="p-8"
                style={{
                  background: '#121212',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  borderRadius: '0px',
                }}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current" style={{ color: '#D4AF37' }} />
                  ))}
                </div>
                <p className="text-lg mb-6" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                  {testimonial.content}
                </p>
                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center font-semibold"
                    style={{ background: '#0EA5FF', color: '#000000' }}
                  >
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-semibold" style={{ color: '#FFFFFF' }}>
                      {testimonial.name}
                    </div>
                    <div className="text-sm" style={{ color: '#4D4D4D' }}>
                      {testimonial.role}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={{ padding: '100px 7.6923%', background: '#000000' }}>
        <div className="container mx-auto text-center">
          <h2
            className="text-5xl font-semibold mb-8"
            style={{ color: '#FFFFFF', fontWeight: 600, letterSpacing: '-0.02em' }}
          >
            Get in Touch
          </h2>
          <p className="text-xl mb-12" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
            Join our community and get instant support
          </p>

          <div className="flex flex-wrap justify-center gap-6">
            <Button
              onClick={() => window.open('https://discord.gg/CTKwc5vcMy', '_blank')}
              style={{
                background: '#5865F2',
                color: '#FFFFFF',
                borderRadius: '0px',
                padding: '16px 28px',
                fontSize: '18px',
                fontWeight: 500,
                minHeight: '56px',
                border: 'none',
              }}
            >
              <MessageSquare className="w-5 h-5 mr-2 inline" />
              Join Discord Community
            </Button>
            <Button
              onClick={() => window.open('https://www.youtube.com/@silentytzx2', '_blank')}
              style={{
                background: '#FF0000',
                color: '#FFFFFF',
                borderRadius: '0px',
                padding: '16px 28px',
                fontSize: '18px',
                fontWeight: 500,
                minHeight: '56px',
                border: 'none',
              }}
            >
              <Youtube className="w-5 h-5 mr-2 inline" />
              YouTube Channel
            </Button>
          </div>

          {/* Discord Discount Banner */}
          <div className="mt-12 p-6 glass" style={{ borderRadius: '0px', border: '1px solid rgba(0, 255, 209, 0.3)' }}>
            <div className="flex items-center justify-center gap-3 mb-3">
              <Tag className="w-6 h-6" style={{ color: '#00FFD1' }} />
              <span className="text-lg font-semibold" style={{ color: '#FFFFFF' }}>Special Offer!</span>
            </div>
            <p className="text-lg mb-4" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
              Join our main Discord channel and get <span style={{ color: '#00FFD1', fontWeight: 'bold' }}>15% discount</span> on all premium plugins!
            </p>
            <Button
              onClick={() => window.open('https://discord.gg/CTKwc5vcMy', '_blank')}
              style={{
                background: 'rgba(0, 255, 209, 0.1)',
                color: '#00FFD1',
                borderRadius: '0px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: 500,
                border: '1px solid rgba(0, 255, 209, 0.3)',
              }}
            >
              <MessageSquare className="w-4 h-4 mr-2 inline" />
              Get 15% Discount
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        style={{
          background: '#000000',
          borderTop: '1px solid rgba(255, 255, 255, 0.25)',
          padding: '60px 7.6923%',
        }}
      >
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-6 h-6" style={{ color: '#00FFD1' }} />
                <span className="text-xl font-semibold" style={{ color: '#FFFFFF' }}>
                  Elite Plugin Hub
                </span>
              </div>
              <p style={{ color: '#4D4D4D' }}>Premium Minecraft plugins and custom development.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4" style={{ color: '#FFFFFF' }}>
                Products
              </h3>
              <ul className="space-y-2">
                <li>
                  <a href="#plugins" style={{ color: '#4D4D4D' }}>
                    Free Plugins
                  </a>
                </li>
                <li>
                  <a href="#plugins" style={{ color: '#4D4D4D' }}>
                    Premium Plugins
                  </a>
                </li>
                <li>
                  <a href="#custom-work" style={{ color: '#4D4D4D' }}>
                    Custom Work
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4" style={{ color: '#FFFFFF' }}>
                Support
              </h3>
              <ul className="space-y-2">
                <li>
                  <a 
                    href="https://discord.gg/CTKwc5vcMy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#4D4D4D' }}
                  >
                    Discord Community
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.youtube.com/@silentytzx2" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#4D4D4D' }}
                  >
                    YouTube Tutorials
                  </a>
                </li>
                <li>
                  <a 
                    href="https://discord.gg/CTKwc5vcMy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#00FFD1' }}
                  >
                    🎁 15% Discount Channel
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4" style={{ color: '#FFFFFF' }}>
                Legal
              </h3>
              <ul className="space-y-2">
                <li>
                  <a href="#" style={{ color: '#4D4D4D' }}>
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a href="#" style={{ color: '#4D4D4D' }}>
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="#" style={{ color: '#4D4D4D' }}>
                    Refund Policy
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div
            className="pt-8 text-center"
            style={{ borderTop: '1px solid rgba(255, 255, 255, 0.25)', color: '#4D4D4D' }}
          >
            <p>&copy; 2025 Elite Plugin Hub. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* CSS Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }

        @keyframes particle-float-0 {
          0%, 100% { transform: translate(0, 0); opacity: 0.6; }
          50% { transform: translate(30px, -40px); opacity: 1; }
        }

        @keyframes particle-float-1 {
          0%, 100% { transform: translate(0, 0); opacity: 0.6; }
          50% { transform: translate(-40px, 30px); opacity: 1; }
        }

        @keyframes particle-float-2 {
          0%, 100% { transform: translate(0, 0); opacity: 0.6; }
          50% { transform: translate(50px, 20px); opacity: 1; }
        }

        @keyframes particle-float-3 {
          0%, 100% { transform: translate(0, 0); opacity: 0.6; }
          50% { transform: translate(-30px, -50px); opacity: 1; }
        }

        @keyframes particle-float-4 {
          0%, 100% { transform: translate(0, 0); opacity: 0.6; }
          50% { transform: translate(40px, -30px); opacity: 1; }
        }

        @keyframes particle-float-5 {
          0%, 100% { transform: translate(0, 0); opacity: 0.6; }
          50% { transform: translate(-50px, 40px); opacity: 1; }
        }

        .hero-glow {
          text-shadow: 0 0 40px rgba(14, 165, 255, 0.5);
        }

        .plugin-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 60px rgba(14, 165, 255, 0.3);
        }

        .feature-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 40px rgba(14, 165, 255, 0.2);
        }

        .btn-primary:hover {
          background: rgba(0, 255, 209, 0.1) !important;
          color: #00FFD1 !important;
          box-shadow: 0 0 30px rgba(0, 255, 209, 0.5);
        }

        input:focus, textarea:focus {
          outline: none;
          border-color: #0EA5FF !important;
          box-shadow: 0 0 0 2px rgba(14, 165, 255, 0.2);
        }
      `}</style>
    </div>
  );
};

export default Home;